import React from "react";
import {
  ShieldCheck, TrendingDown, AlertTriangle, GraduationCap, ListChecks,
  FileX2, Scale, AlertOctagon, Wrench,
} from "lucide-react";

const INDICATORS = [
  { code: "UNAPPROVED_REQUIRED_KPI_PERIOD", key: "KPI Approval", icon: TrendingDown, sub: "Required periods approved" },
  { code: "MISSING_MANDATORY_CONTROLLED_DOCUMENT", key: "Controlled Documents", icon: FileX2, sub: "Mandatory documents current" },
  { code: "OVERDUE_MAJOR_NCR", key: "Major NCR", icon: AlertOctagon, sub: "No overdue Major NCR" },
  { code: "OVERDUE_CRITICAL_ACTION", key: "Critical Actions", icon: ListChecks, sub: "No overdue critical actions" },
  { code: "EXPIRED_CRITICAL_TRAINING", key: "Critical Training", icon: GraduationCap, sub: "Mandatory training valid" },
  { code: "EXPIRED_CRITICAL_CERTIFICATE", key: "Critical Certificates", icon: Wrench, sub: "Equipment certificates valid" },
  { code: "OVERDUE_LEGAL_OBLIGATION", key: "Legal Obligations", icon: Scale, sub: "No overdue obligations" },
];

const clearTone = { label: "Clear", color: "#18B84F", ring: "#DCFCE7", text: "#166534" };
const blockedTone = { label: "Blocked", color: "#EF4444", ring: "#FEE2E2", text: "#7F1D1D" };

export default function CorporateHseScore({ auditReadiness, loading = false }) {
  if (!auditReadiness) {
    return (
      <div className="command-card p-5 text-sm text-slate-500" data-testid="audit-readiness-loading">
        {loading ? "Reconciling audit readiness with authoritative records…" : "Audit readiness is unavailable. No readiness claim is shown."}
      </div>
    );
  }

  const counts = auditReadiness.blockerCounts || {};
  const totalScore = Number(auditReadiness.score ?? auditReadiness.auditReadyScore ?? 0);
  const overall = auditReadiness.isAuditReady
    ? { ...clearTone, label: "Audit Ready" }
    : { ...blockedTone, label: "Audit Blocked" };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="hse-score-section">
      <div className="command-card p-5 lg:col-span-1" data-testid="hse-score-card" style={{ borderTop: `4px solid ${overall.color}` }}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Authoritative Audit Readiness</div>
            <div className="font-heading font-bold text-[15px] text-[#082B4D]">Boardroom Index</div>
          </div>
          <ShieldCheck className="w-5 h-5" style={{ color: overall.color }} />
        </div>
        <div className="flex items-end gap-3">
          <div className="font-heading font-bold text-[54px] leading-none tabular" style={{ color: overall.color }} data-testid="hse-score-value">{totalScore}</div>
          <div className="text-slate-400 text-[13px] font-mono mb-2">/ 100</div>
        </div>
        <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider" style={{ background: overall.ring, color: overall.text }} data-testid="hse-score-label">
          {auditReadiness.isAuditReady ? <ShieldCheck className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          {overall.label}
        </div>
        <div className="mt-4 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Seven authoritative gates</div>
        <div className="mt-2 space-y-1.5" data-testid="hse-score-breakdown">
          {INDICATORS.map((indicator) => {
            const count = Number(counts[indicator.code] || 0);
            return (
              <a key={indicator.code} href={auditReadiness.sourceRoutes?.[indicator.code] || "#"} className="flex items-center gap-2 rounded hover:bg-slate-50 px-1 py-0.5">
                <indicator.icon className="w-3 h-3 text-slate-400 shrink-0" />
                <div className="text-[11px] text-slate-700 flex-1 truncate">{indicator.key}</div>
                <div className={`text-[10px] font-semibold ${count ? "text-rose-700" : "text-emerald-700"}`}>{count ? `${count} blocker(s)` : "Clear"}</div>
              </a>
            );
          })}
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] text-slate-400 font-mono" data-testid="audit-ready-as-of">
          Server data as of {new Date(auditReadiness.dataAsOf).toLocaleString()}
        </div>
      </div>

      <div className="command-card p-5 lg:col-span-2" data-testid="audit-readiness-section">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#18B84F] font-semibold">ISO 45001 · Audit Readiness</div>
            <div className="font-heading font-bold text-[15px] text-[#082B4D]">Corporate Snapshot</div>
          </div>
          <div className={`text-[11px] font-bold ${auditReadiness.isAuditReady ? "text-emerald-700" : "text-rose-700"}`} data-testid="audit-ready-card">
            {auditReadiness.isAuditReady ? "AUDIT READY" : "NOT AUDIT READY"}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
          {INDICATORS.map((indicator) => {
            const count = Number(counts[indicator.code] || 0);
            const state = count ? blockedTone : clearTone;
            return (
              <a key={indicator.code} href={auditReadiness.sourceRoutes?.[indicator.code] || "#"} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:shadow-sm transition" data-testid={`readiness-${indicator.code.toLowerCase().replace(/_/g, "-")}`}>
                <div className="flex items-center justify-between mb-1"><indicator.icon className="w-3.5 h-3.5 text-slate-500" /><span className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: state.color }}>{state.label}</span></div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate">{indicator.key}</div>
                <div className="font-heading font-bold text-[22px] tabular mt-0.5" style={{ color: state.color }}>{count ? 0 : 100}%</div>
                <div className="text-[10px] text-slate-400 mt-1 truncate">{count ? `${count} blocker(s)` : indicator.sub}</div>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
